import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChartPie, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaPercentage } from 'react-icons/fa';

const Statistic = ({ isOpen, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/predictions/accuracy/stats');
        const data = await response.json();
        
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Could not fetch statistics.');
        }

        setStats(data.data);
      } catch (err) {
        setError(err.message);
        console.error("Statistics fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchStats();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const StatCard = ({ icon, title, value, colorClass }) => (
    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl flex items-center space-x-4">
      <div className={`p-3 rounded-lg ${colorClass}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          <motion.div
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <FaChartPie className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Prediction Statistics
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Analysis of our predictions accuracy
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close"
              >
                 <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 flex-grow overflow-y-auto">
              {loading && (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              )}

              {error && (
                <div className="flex justify-center items-center h-full text-center">
                  <p className="text-red-500 dark:text-red-400">{error}</p>
                </div>
              )}
              
              {!loading && !error && stats && typeof stats.accuracy !== 'undefined' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard 
                      icon={<FaPercentage className="w-6 h-6 text-green-600 dark:text-green-400" />}
                      title="Accuracy"
                      value={`${stats.accuracy.toFixed(2)}%`}
                      colorClass="bg-green-100 dark:bg-green-900/30"
                    />
                    <StatCard 
                      icon={<FaCheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
                      title="Correct"
                      value={stats.correctPredictions}
                      colorClass="bg-blue-100 dark:bg-blue-900/30"
                    />
                    <StatCard 
                      icon={<FaTimesCircle className="w-6 h-6 text-red-600 dark:text-red-400" />}
                      title="Incorrect"
                      value={stats.incorrectPredictions}
                      colorClass="bg-red-100 dark:bg-red-900/30"
                    />
                     <StatCard 
                      icon={<FaHourglassHalf className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />}
                      title="Pending"
                      value={stats.pendingPredictions}
                      colorClass="bg-yellow-100 dark:bg-yellow-900/30"
                    />
                     <StatCard 
                      icon={<FaChartPie className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />}
                      title="Total Checked"
                      value={stats.totalChecked}
                      colorClass="bg-indigo-100 dark:bg-indigo-900/30"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Recent Results
                    </h4>
                    <div className="space-y-3">
                      {stats.breakdown.map((item) => (
                        <div key={item.key} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          <div className="flex justify-between items-center">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">{item.type}</p>
                            <div className="flex items-center space-x-4">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {item.correct} / {item.total}
                              </p>
                              <p className={`text-lg font-bold ${item.correct ? 'text-green-500' : 'text-red-500'}`}>
                                {item.correct ? '✓' : '✗'}
                              </p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mt-2">
                            <div 
                              className={`${item.correct ? 'bg-green-500' : 'bg-red-500'} h-2.5 rounded-full`} 
                              style={{ width: `${item.accuracy}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Statistic; 